import * as React from "react";
import { Link, Navigate, useSearch } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "sonner";
import { MailCheck, RotateCw } from "lucide-react";

import { AuthCard, FormError } from "@/components/auth";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { readLastSentAt, recordSent, secondsUntilResend } from "@/lib/resend-cooldown";
import { sessionStore } from "./session-store";

/**
 * "Check your inbox" page shown after signing up while email confirmation is
 * on. Shows where the link went, what to do next, a rate-limited resend and a
 * way back to fix the address.
 */
export function CheckEmailPage() {
  const { email } = useSearch({ strict: false }) as { email?: string };
  if (!email) return <Navigate to="/signup" replace />;
  return <CheckEmail email={email} />;
}

function CheckEmail({ email }: { email: string }) {
  const { t } = useTranslation("auth");
  const { resendSignupConfirmation } = useAuth();

  const [lastSentAt, setLastSentAt] = React.useState(() => readLastSentAt(sessionStore(), email));
  const [now, setNow] = React.useState(() => Date.now());
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const secondsLeft = secondsUntilResend(lastSentAt, now);

  React.useEffect(() => {
    if (secondsLeft === 0) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [secondsLeft]);

  const handleResend = async () => {
    setError(null);
    setSending(true);
    try {
      await resendSignupConfirmation(email);
      const sentAt = Date.now();
      recordSent(sessionStore(), email, sentAt);
      setLastSentAt(sentAt);
      setNow(sentAt);
      toast.success(t("checkEmail.resent"));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t("checkEmail.resendFailed"));
    } finally {
      setSending(false);
    }
  };

  const steps = [t("checkEmail.step1"), t("checkEmail.step2"), t("checkEmail.step3")];

  return (
    <AuthCard
      title={t("checkEmail.title")}
      footer={
        <>
          {t("checkEmail.confirmed")}{" "}
          <Link to="/login" className="font-medium text-primary hover:underline">
            {t("signup.signIn")}
          </Link>
        </>
      }
    >
      <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/40 px-4 py-3.5">
        <MailCheck className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden />
        <p className="text-[15px] leading-relaxed text-muted-foreground">
          <Trans
            t={t}
            i18nKey="checkEmail.sentTo"
            values={{ email }}
            components={{
              email: (
                <strong
                  className="font-semibold break-all text-foreground"
                  data-testid="check-email-address"
                />
              ),
            }}
          />
        </p>
      </div>

      <ol className="space-y-2.5 pt-2">
        {steps.map((step, i) => (
          <li key={i} className="flex items-start gap-3 text-[15px] leading-relaxed">
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
              {i + 1}
            </span>
            <span className="pt-px">{step}</span>
          </li>
        ))}
      </ol>

      <FormError message={error} />

      <div className="space-y-3 pt-2">
        <Button
          type="button"
          variant="outline"
          className="h-11 w-full"
          disabled={sending || secondsLeft > 0}
          onClick={handleResend}
        >
          <RotateCw className={sending ? "animate-spin" : undefined} aria-hidden />
          {secondsLeft > 0
            ? t("checkEmail.resendIn", { seconds: secondsLeft })
            : sending
              ? t("checkEmail.resending")
              : t("checkEmail.resend")}
        </Button>

        <p className="text-sm text-muted-foreground">
          {t("checkEmail.wrongAddress")}{" "}
          <Link
            to="/signup"
            search={{ email }}
            className="font-medium text-primary hover:underline"
          >
            {t("checkEmail.changeEmail")}
          </Link>
        </p>
      </div>
    </AuthCard>
  );
}

export default CheckEmailPage;
